"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransactionService = void 0;
const utils_1 = require("ethers/lib/utils");
const __1 = require("..");
const utils_2 = require("../utils");
class TransactionService {
    constructor(contractsMap, providerMap, globalService, transactionStorage, offChainStorage) {
        this.contractsMap = contractsMap;
        this.providerMap = providerMap;
        this.globalService = globalService;
        this.transactionStorage = transactionStorage;
        this.offChainStorage = offChainStorage;
        this.DEFAULT_BORROW_SCORE_NAME = 'arcx.credit';
    }
    fetchPendingTransactions(chainId, account, protocol) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.transactionStorage.readPendingTransactions(chainId, account, protocol);
        });
    }
    removePendingTransactions(chainId, transactionIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.transactionStorage.deletePendingTransactions(chainId, transactionIds);
        });
    }
    publishPendingTransactions(chainId, pendingTransactions) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.transactionStorage.createPendingTransactions(chainId, pendingTransactions);
        });
    }
    refreshPendingTransactions(chainId, pendingTransactions) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.transactionStorage.updatePendingTransactions(chainId, pendingTransactions);
        });
    }
    fetchAndParsePendingExplanations(chainId, account, protocol, scoreExplanations) {
        return __awaiter(this, void 0, void 0, function* () {
            const transactionIdsToRemove = [];
            const pendingExplanations = [];
            const scoreRelatedPendingTransactions = yield this.fetchPendingTransactions(chainId, account, protocol);
            for (const pendingTransaction of scoreRelatedPendingTransactions) {
                const existingExplanation = scoreExplanations.find((explanation) => explanation.transactionId === pendingTransaction.transaction_id);
                if (existingExplanation) {
                    transactionIdsToRemove.push(pendingTransaction.transaction_id);
                }
                else {
                    const currentEpochDelaySecs = (yield this.globalService.fetchGlobals(chainId))
                        .current_epoch_delay_secs;
                    pendingExplanations.push(this.generatePendingScoreExplanation(pendingTransaction, currentEpochDelaySecs, chainId));
                }
            }
            if (transactionIdsToRemove.length > 0) {
                yield this.removePendingTransactions(chainId, transactionIdsToRemove);
            }
            return pendingExplanations;
        });
    }
    fetchPendingTransactionsFromTransactionIds(chainId, transactionIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.transactionStorage.readPendingTransactionsFromTransactionIds(chainId, transactionIds);
        });
    }
    processPendingTransactionsFromParsedLogs(borrowChainId, protocol = this.DEFAULT_BORROW_SCORE_NAME) {
        return __awaiter(this, void 0, void 0, function* () {
            const contractProperties = (0, __1.getBorrowContracts)(borrowChainId, this.contractsMap.borrowCore);
            const blockchains = (yield this.offChainStorage.readBlockchains(borrowChainId)) || [];
            for (const contractProperty of contractProperties) {
                const { chainId, contract, address } = contractProperty;
                const blockchain = blockchains.find((blockchain) => blockchain.chain_id === chainId);
                const latestBlockScanned = blockchain === null || blockchain === void 0 ? void 0 : blockchain.latest_block_scanned;
                const provider = this.providerMap[chainId];
                const filter = this.generateBorrowContractFilters(contract, address, latestBlockScanned);
                const logs = yield provider.getLogs(filter);
                const transactionIds = logs.map((log) => log.transactionHash);
                const existingPendingTransactions = yield this.fetchPendingTransactionsFromTransactionIds(chainId, transactionIds);
                const pendingTransactionsToUpdate = [];
                const pendingTransactionsToCreate = [];
                for (const log of logs) {
                    const parsedLog = contract.interface.parseLog(log);
                    const logName = parsedLog.name;
                    const logTransactionHash = log.transactionHash;
                    const correspondingPendingTransaction = existingPendingTransactions.find((pendingTransaction) => {
                        return (pendingTransaction.transaction_id === logTransactionHash &&
                            pendingTransaction.method === __1.BorrowMethodMapping[logName]);
                    });
                    if (correspondingPendingTransaction) {
                        const blockNumber = log.blockNumber;
                        const timestamp = (yield provider.getBlock(blockNumber)).timestamp;
                        pendingTransactionsToUpdate.push(Object.assign(Object.assign({}, correspondingPendingTransaction), { timestamp, block_number: blockNumber }));
                    }
                    else {
                        const logArgs = parsedLog.args;
                        const account = logArgs._user.toLowerCase();
                        const newPendingTransaction = new __1.PendingTransaction(account, logTransactionHash, protocol, __1.BorrowMethodMapping[logName], chainId, '', 0, '', 0, 0);
                        pendingTransactionsToCreate.push(newPendingTransaction);
                    }
                }
                yield Promise.all([
                    yield this.refreshPendingTransactions(chainId, pendingTransactionsToUpdate),
                    yield this.publishPendingTransactions(chainId, pendingTransactionsToCreate),
                ]);
            }
        });
    }
    generatePendingScoreExplanation(transaction, current_epoch_delay_secs, chainId) {
        const delayText = (0, __1.secondsToTimeframeString)(current_epoch_delay_secs * 2);
        const moneyFormat = (0, utils_2.humanReadableBN)(transaction.amount, transaction.decimals);
        return {
            id: 'pending',
            explanation: `Pending ${transaction.method} transaction of ${moneyFormat} ${transaction.asset}`,
            suggestion: `This transaction will be processed within ${delayText}`,
            impact: '',
            timestamp: `${transaction.timestamp}`,
            transactionId: transaction.transaction_id,
            source: (0, __1.getSourceFromChainId)(chainId),
        };
    }
    generateBorrowContractFilters(contract, address, latestBlockScanned) {
        const contractFilters = Object.keys(contract.filters);
        const actions = Object.keys(__1.BorrowMethodMapping);
        const logTopics = [];
        contractFilters.forEach((contractFilter) => {
            actions.forEach((action) => {
                const regExp = new RegExp(`^${action}\\(.*\\)$`);
                if (regExp.test(contractFilter))
                    logTopics.push((0, utils_1.id)(contractFilter));
            });
        });
        return {
            address: address,
            topics: [logTopics],
            toBlock: 'latest',
            fromBlock: latestBlockScanned || -10, // 10 blocks ago if undefined
        };
    }
}
exports.TransactionService = TransactionService;
//# sourceMappingURL=TransactionService.js.map